import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subject, takeUntil, map, distinctUntilChanged } from "rxjs";
import { LogService } from "../baseServices/logging/log.service";
import { AppUserService } from "./app-user.service";
import { RoleClaimService } from "./role-claim.service";

export enum TimeEntryMethods {
  UNKNOWN = "UNKNOWN",
  CLOCK_ENTRY = "CLOCK_ENTRY",
  BLOCK_ENTRY = "BLOCK_ENTRY",
  NOT_TIME_USER = "NOT_TIME_USER"
}

/** Lightweight service to track the current user's time entry method */
@Injectable({ providedIn: "root" })
export class AppUserTimeEntryMethodService implements OnDestroy {
  private readonly timeEntryMethodSubject = new BehaviorSubject<TimeEntryMethods>(TimeEntryMethods.UNKNOWN);

  /** Observable for the current user's time entry method (CLOCK_ENTRY, BLOCK_ENTRY, NOT_TIME_USER) */
  readonly timeEntryMethod$ = this.timeEntryMethodSubject.asObservable();
  /** Emits true when current user uses clock-based entry */
  readonly isClockTimeEntryUser$ = this.timeEntryMethod$.pipe(
    map(m => m === TimeEntryMethods.CLOCK_ENTRY),
    distinctUntilChanged()
  );
  /** Emits true when current user uses block/manual entry */
  readonly isBlockTimeEntryUser$ = this.timeEntryMethod$.pipe(
    map(m => m === TimeEntryMethods.BLOCK_ENTRY),
    distinctUntilChanged()
  );
  /** Emits true when current user is not a time user */
  readonly isNotTimeUser$ = this.timeEntryMethod$.pipe(
    map(m => m === TimeEntryMethods.NOT_TIME_USER),
    distinctUntilChanged()
  );

  /** Returns the current user's time entry method (CLOCK_ENTRY, BLOCK_ENTRY, NOT_TIME_USER) */
  get timeEntryMethod(): TimeEntryMethods { return this.timeEntryMethodSubject.value; }
  /** Returns true when current user uses clock-based entry */
  get isClockTimeEntryUser(): boolean { return this.timeEntryMethod === TimeEntryMethods.CLOCK_ENTRY; }
  /** Returns true when current user uses block/manual entry */
  get isBlockTimeEntryUser(): boolean { return this.timeEntryMethod === TimeEntryMethods.BLOCK_ENTRY; }
  /** Returns true when current user is not a time user */
  get isNotTimeUser(): boolean { return this.timeEntryMethod === TimeEntryMethods.NOT_TIME_USER; }

  private readonly destroy$ = new Subject<void>();

  constructor(
    private logService: LogService,
    private appUserService: AppUserService,
    private roleClaimsService: RoleClaimService
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSubscriptions(): void {
    // On user change (login/logout)
    this.appUserService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (!user) {
          this.setTimeEntryMethod(TimeEntryMethods.UNKNOWN);
          return;
        }

        if (!user.IsTimeUser) {
          this.setTimeEntryMethod(TimeEntryMethods.NOT_TIME_USER);
          return;
        }

        if (this.roleClaimsService.checkCurrentUserHasManualTimeEntryClaim()) {
          this.setTimeEntryMethod(TimeEntryMethods.BLOCK_ENTRY);
          return;
        }

        this.setTimeEntryMethod(TimeEntryMethods.CLOCK_ENTRY);
      });
  }

  private setTimeEntryMethod(method: TimeEntryMethods): void {
    if (this.timeEntryMethod !== method) {
      this.logService.info("AppUserTimeEntryMethod.SetTimeEntryMethod", { previousMethod: TimeEntryMethods[this.timeEntryMethod], newMethod: TimeEntryMethods[method] });
      this.timeEntryMethodSubject.next(method);
    }
  }
}